import * as React from "react";
import { Stack, TextField, AlertColor } from "@mui/material";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { useAddGlCodeMutation, useModifyGlCodeMutation } from "../../../services/cclTokenedPayrollApi";
import { GlCode } from "../../../services/types/payrollEventTypes";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

export interface AddEditGlCodeDrawerProps {
    open: boolean;
    mode?: "add" | "edit";
    code?: GlCode | null;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const AddEditGlCodeDrawer: React.FC<AddEditGlCodeDrawerProps> = (props) => {
    const isEditMode = props.mode === "edit";
    const [glCode, setGlCode] = React.useState<string>("");
    const [description, setDescription] = React.useState<string>("");
    const [glLetters, setGlLetters] = React.useState<string>("");

    const [addGlCode, { isLoading: isAddLoading }] = useAddGlCodeMutation();
    const [modifyGlCode, { isLoading: isModifyLoading }] = useModifyGlCodeMutation();
    const { logEvent } = useLogAccessEvent();
    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        if (isEditMode && props.code) {
            setGlCode(String(props.code.glCode ?? ""));
            setDescription(props.code.description ?? "");
            setGlLetters(props.code.glLetters ?? "");
            return;
        }

        setGlCode("");
        setDescription("");
        setGlLetters("");
    }, [props.open, isEditMode, props.code]);

    const isSaving = isAddLoading || isModifyLoading;
    const glCodeValue = String(glCode ?? "").trim();
    // The GL Code is an integer key from the General Ledger (D365).
    const isValidGlCode = /^\d+$/.test(glCodeValue) && Number(glCodeValue) > 0;
    const canSave =
        (isEditMode || isValidGlCode) &&
        description.trim() !== "" &&
        glLetters.trim() !== "" &&
        !isSaving;

    const handleSave = () => {
        const payload: Partial<GlCode> & { glCode?: string } = {
            glCode: glCodeValue,
            description: description.trim(),
            glLetters: glLetters.trim(),
            active: true,
        };

        const saveAction = isEditMode ? modifyGlCode(payload) : addGlCode(payload);

        saveAction
            .unwrap()
            .then((response) => {
                logEvent(isEditMode ? "GLCodeEdited" : "GLCodeAdded", null);
                props.onSaveResult(isEditMode ? "GL Code Edited" : "GL Code Added", "success");
                props.onClose();
            })
            .catch((error) => {
                console.error("Error saving GL code:", error);
                props.onSaveResult(
                    isEditMode ? "Failed to edit GL Code" : "Failed to add GL Code",
                    "error"
                );
            });
    };

    return (
        <CclDrawer
            title={isEditMode ? "Edit GL Code" : "Add GL Code"}
            open={props.open}
            onClose={props.onClose}
        >
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    id="add-edit-gl-code-code"
                    label="Code"
                    size="small"
                    fullWidth
                    value={glCode}
                    disabled={isEditMode}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={!isEditMode && glCodeValue !== "" && !isValidGlCode}
                    helperText={
                        !isEditMode && glCodeValue !== "" && !isValidGlCode
                            ? "Enter the numeric GL Code (digits only)."
                            : undefined
                    }
                    onChange={(e) => setGlCode(e.target.value.replace(/\D/g, ""))}
                />
                <TextField
                    id="add-edit-gl-code-description"
                    label="Description"
                    size="small"
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <TextField
                    id="add-edit-gl-code-letters"
                    label="Letters"
                    size="small"
                    fullWidth
                    value={glLetters}
                    onChange={(e) => setGlLetters(e.target.value)}
                />
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode="primary"
                        loading={isSaving}
                        disabled={!canSave}
                        onClick={handleSave}
                    >
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Close
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddEditGlCodeDrawer;